// Layer 3 (Transformation): upstream change detection for case cards.

import type { Case } from '@/versions/v1/schema/case';
import type { DiagnosticRule } from '@/versions/v1/schema/diagnostic-rule';
import type { SignalRole } from '@/versions/v1/schema/tag-mapping';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { ADVISORY_RULES, ISSUE_THREADS, VARIATION_EVENTS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { DIAGNOSTIC_RULES } from '@/versions/v1/shared/bunker-hill-seed';
import { MET_DIAGNOSTICS_SECTION_RULES } from '@/versions/v1/shared/bunker-hill-met-diagnostics-rules';
import { TAG_MAPPING, MONITORED_VARIABLES } from '@/versions/v1/shared/bunker-hill-spc';
import { formatClockShort, parseTime } from '@/versions/v1/shared/clock.derive';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { seriesAt } from '@/versions/v1/shared/series.derive';
import type { Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import { upstreamNodes, variableIdForCase } from '@/versions/v1/shared/threads';

const ALL_RULES: readonly DiagnosticRule[] = [
  ...DIAGNOSTIC_RULES,
  ...ADVISORY_RULES,
  ...MET_DIAGNOSTICS_SECTION_RULES,
];

const RULE_BY_ID = new Map(ALL_RULES.map((row) => [row.id, row]));
const MAPPING_BY_ROLE = new Map(TAG_MAPPING.map((row) => [row.role, row]));

export type UpstreamChange = {
  label: string;
  at: SeriesTime;
  offsetMinutes: number;
};

export type UpstreamContext = {
  cases: readonly Case[];
  diagnosis: Diagnosis;
};

const stepCache = new Map<string, { at: SeriesTime; direction: 'fell' | 'rose' } | null>();

function resolveDetectedAt(caseItem: Case, cursor: SeriesTime): SeriesTime {
  const event = caseItem.variationEventId
    ? VARIATION_EVENTS.find((row) => row.id === caseItem.variationEventId)
    : undefined;
  const raw = event?.detectedAt ?? caseItem.raisedAt;
  if (raw.includes('T')) return raw as SeriesTime;
  const timeOnly = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (timeOnly) {
    const date = cursor.split('T')[0];
    return `${date}T${timeOnly[1]!.padStart(2, '0')}:${timeOnly[2]}` as SeriesTime;
  }
  return caseItem.raisedAt.includes('T') ? (caseItem.raisedAt as SeriesTime) : cursor;
}

function subprocessForRole(role: SignalRole): string | undefined {
  return MAPPING_BY_ROLE.get(role)?.subprocessId;
}

function subprocessForCase(caseItem: Case): string | undefined {
  const variableId = variableIdForCase(caseItem);
  if (!variableId) return undefined;
  const monitored = MONITORED_VARIABLES.find((row) => row.id === variableId);
  if (!monitored) return undefined;
  return TAG_MAPPING.find((row) => row.seriesId === monitored.seriesId)?.subprocessId;
}

function signalRoleForVariable(variableId: string): SignalRole | undefined {
  const monitored = MONITORED_VARIABLES.find((row) => row.id === variableId);
  if (!monitored) return undefined;
  return TAG_MAPPING.find((row) => row.seriesId === monitored.seriesId)?.role;
}

function signalRolesForThreadCase(caseItem: Case): SignalRole[] {
  const variableId = variableIdForCase(caseItem);
  if (variableId) {
    const role = signalRoleForVariable(variableId);
    return role ? [role] : [];
  }
  if (caseItem.id === 'case-smbs-fmea') return ['reagent-smbs-rate'];
  return [];
}

function rolesFromRule(caseItem: Case): SignalRole[] {
  const event = caseItem.variationEventId
    ? VARIATION_EVENTS.find((row) => row.id === caseItem.variationEventId)
    : undefined;
  const ruleId = event?.matchedRules[0]?.ruleId;
  const rule = ruleId ? RULE_BY_ID.get(ruleId) : undefined;
  if (!rule?.when || rule.when.length <= 1) return [];
  return rule.when.slice(1).map((clause) => clause.signal);
}

function rolesFromThread(caseItem: Case, _context: UpstreamContext): SignalRole[] {
  const caseSub = subprocessForCase(caseItem);
  if (!caseSub) return [];
  const upstreamSubs = upstreamNodes(caseSub);
  const thread = ISSUE_THREADS.find((row) => row.caseIds.includes(caseItem.id));
  if (!thread) return [];

  const roles: SignalRole[] = [];
  for (const caseId of thread.caseIds) {
    if (caseId === caseItem.id) continue;
    const peer = _context.cases.find((row) => row.id === caseId);
    if (!peer) continue;
    for (const role of signalRolesForThreadCase(peer)) {
      const roleSub = subprocessForRole(role);
      if (roleSub && upstreamSubs.has(roleSub) && roleSub !== caseSub) {
        roles.push(role);
      }
    }
  }
  return roles;
}

function upstreamRolesForCase(caseItem: Case, context?: UpstreamContext): SignalRole[] {
  const seen = new Set<SignalRole>();
  const ordered: SignalRole[] = [];
  for (const role of rolesFromRule(caseItem)) {
    if (!seen.has(role)) {
      seen.add(role);
      ordered.push(role);
    }
  }
  if (context) {
    for (const role of rolesFromThread(caseItem, context)) {
      if (!seen.has(role)) {
        seen.add(role);
        ordered.push(role);
      }
    }
  }
  return ordered;
}

function levelShiftOnce(
  points: readonly { at: SeriesTime; value: number }[],
  cursor: SeriesTime,
  lookbackHours = 8,
): { at: SeriesTime; direction: 'fell' | 'rose' } | null {
  const cursorMs = parseTime(cursor).getTime();
  const cutoff = cursorMs - lookbackHours * 60 * 60 * 1000;
  const window = points.filter((row) => {
    const ms = parseTime(row.at).getTime();
    return ms >= cutoff && ms <= cursorMs;
  });
  if (window.length < 3) return null;

  for (let i = 2; i < window.length; i++) {
    const trailing = window.slice(Math.max(0, i - 4), i);
    if (trailing.length < 2) continue;
    const after = window.slice(i, Math.min(i + 2, window.length));
    const meanBefore = trailing.reduce((sum, row) => sum + row.value, 0) / trailing.length;
    const meanAfter = after.reduce((sum, row) => sum + row.value, 0) / after.length;
    const shift = meanAfter - meanBefore;
    const mag = Math.abs(shift);
    if (mag < 0.03 * Math.max(Math.abs(meanBefore), 1)) continue;
    return {
      at: window[i]!.at,
      direction: shift < 0 ? 'fell' : 'rose',
    };
  }
  return null;
}

function stepForSeries(
  seriesId: string,
  state: SessionState,
): { at: SeriesTime; direction: 'fell' | 'rose' } | null {
  const cacheKey = `${seriesId}|${state.cursor}`;
  if (stepCache.has(cacheKey)) return stepCache.get(cacheKey) ?? null;

  const view = seriesAt(seriesId, state);
  const numeric = view.points.filter(
    (row): row is { at: SeriesTime; value: number } =>
      row.value !== null && row.value !== undefined,
  );
  const shift = levelShiftOnce(numeric, state.cursor);
  stepCache.set(cacheKey, shift);
  return shift;
}

function labelForRole(role: SignalRole): string {
  const mapping = MAPPING_BY_ROLE.get(role);
  if (!mapping?.readingId) return role;
  const reading = readingByIdLookup(mapping.readingId);
  return reading?.shortLabel ?? reading?.label ?? mapping.instrumentTag ?? role;
}

export function upstreamChangeForCase(
  caseItem: Case,
  state: SessionState,
  context?: UpstreamContext,
): UpstreamChange | null {
  const roles = upstreamRolesForCase(caseItem, context);
  if (roles.length === 0) return null;

  const detectedAt = resolveDetectedAt(caseItem, state.cursor);
  const detectedMs = parseTime(detectedAt).getTime();

  for (const role of roles) {
    const mapping = MAPPING_BY_ROLE.get(role);
    if (!mapping?.seriesId) continue;
    const shift = stepForSeries(mapping.seriesId, state);
    if (!shift) continue;
    const stepMs = parseTime(shift.at).getTime();
    const offsetMinutes = Math.round((detectedMs - stepMs) / 60_000);
    if (offsetMinutes < 0) continue;
    const hm = shift.at.split('T')[1]?.slice(0, 5) ?? formatClockShort(shift.at);
    return {
      label: `${labelForRole(role)} ${shift.direction} ${hm}`,
      at: shift.at,
      offsetMinutes,
    };
  }
  return null;
}

/** Reset step cache between probe runs. */
export function resetUpstreamStepCache(): void {
  stepCache.clear();
}
